// Angular
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Component Library
import type { FsxCustomError } from 'fsx-component-library';

/**
 * Characters allowed while typing/pasting into a phone number field: digits
 * plus the formatting punctuation in the phone number spec (#169520) — "(", ")", "-", ".".
 * Pair with `AllowedInputCharsDirective` (`[appAllowedInputChars]`).
 */
export const PHONE_ALLOWED_CHARS = /[0-9().-]/;

/**
 * Characters allowed while typing/pasting into a phone extension field: digits only.
 * Pair with `AllowedInputCharsDirective` (`[appAllowedInputChars]`).
 */
export const PHONE_EXTENSION_ALLOWED_CHARS = /[0-9]/;

/**
 * Maximum phone extension length, per the phone number spec (#169520).
 */
export const PHONE_EXTENSION_MAX_LENGTH = 10;

/**
 * The validators every phone extension control uses: numeric only, at most
 * {@link PHONE_EXTENSION_MAX_LENGTH} digits.
 *
 * Raises its own keys rather than Angular's `pattern` / `maxlength`: fsx-input
 * always renders a generic built-in message for those, and `customErrors` can
 * only add to it, never replace it. Pair with {@link PHONE_EXTENSION_CUSTOM_ERRORS}.
 */
export function phoneExtensionValidators(): ValidatorFn[] {
  return [
    (control: AbstractControl): ValidationErrors | null => {
      const extension: string | null = control.value;
      if (!extension) {
        return null;
      }
      if (!/^\d+$/.test(extension)) {
        return { phoneExtensionNotNumeric: true };
      }
      if (extension.length > PHONE_EXTENSION_MAX_LENGTH) {
        return { phoneExtensionTooLong: true };
      }
      return null;
    },
  ];
}

/**
 * Messages for the error keys {@link phoneExtensionValidators} raises. fsx-input
 * shows no text for these unless each Ext. field passes them as `customErrors`.
 */
export const PHONE_EXTENSION_CUSTOM_ERRORS: FsxCustomError[] = [
  {
    error: 'phoneExtensionNotNumeric',
    message: 'Ext. must contain only numbers',
  },
  {
    error: 'phoneExtensionTooLong',
    message: `Ext. must be ${PHONE_EXTENSION_MAX_LENGTH} digits or fewer`,
  },
];

/**
 * The message for the `phoneInvalidFormat` error {@link phoneNumberValidator}
 * raises. fsx-input shows no text for an error key it isn't handed a message
 * for; `app-phone-input` supplies this entry itself, so its consumers don't.
 */
export const PHONE_INVALID_FORMAT_ERROR: FsxCustomError = {
  error: 'phoneInvalidFormat',
  message: 'Phone Number must be 10 digits',
};

/**
 * A custom validator to check if the phone number is valid.
 * Validates that the phone number is exactly 10 digits and contains only numbers.
 *
 * Accepts punctuation even though `app-phone-input` writes digits only: a value
 * loaded from the backend (including older formatted ones like `410-123-1451`)
 * stays in the control untouched until the user edits it, and must not show as
 * invalid on load. Only the digit count is the real rule.
 *
 * @returns A ValidatorFn that returns a ValidationErrors object if the phone number is invalid, otherwise null
 */
export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const phoneNumber = control.value;

    // If no phone number value, return null (let required validator handle it)
    if (!phoneNumber || !phoneNumber.trim()) {
      return null;
    }

    // Remove any whitespace
    const trimmedPhoneNumber = phoneNumber.trim();

    // Check if phone number contains only numbers
    if (!/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(trimmedPhoneNumber)) {
      return { phoneInvalidFormat: true };
    }

    // No error found, phone number is valid
    return null;
  };
}

